/**
 * UI/UX's service (Figma's): whether UI/UX is worked in, prototype mode, the screens (top-level
 * frames with a device), where a click leads, the screen played first, components and their
 * instances, and the prototype (`prototype/`). Every change is one graph edit (one undo), made
 * through the board's and the graph's own services.
 */
import type { Accessor } from "solid-js";
import { createMemo } from "solid-js";
import { unwrap } from "solid-js/store";
import type { GraphEdit } from "@protocol/GraphEdit";
import type { Actions } from "../../client/actions";
import type { GraphMirror } from "../../client/graph";
import { toast } from "../../shared/toast";
import { asPatch, carried, frameAround, rounded, topZ, type Board, type BoardFrame, type Point } from "../../state/board";
import type { KindsService } from "../../state/kinds";
import { DESIGN, type PackContext } from "../../state/packs";
import type { View } from "../../state/view";
import { DEVICES, INSTANCE_GAP } from "./constants";
import type { PrototypeService } from "./prototype";
import { screensOf } from "./screens";

/** UI/UX's board mode: where each click leads is drawn and set, instead of the design. */
export const PROTOTYPE_MODE = "prototype";

/** UI/UX's service. */
export interface UiUx {
  /** Whether UI/UX is worked in: frames are screens, a click may lead somewhere. */
  active: Accessor<boolean>;
  /** Prototype mode (Figma's): where each click leads is drawn and set, instead of the design. */
  prototyping: Accessor<boolean>;
  setPrototyping(on: boolean): void;
  /** The screens: top-level frames that are not components, left to right. */
  screens: Accessor<BoardFrame[]>;
  /** A click on item `id` leads to the screen `to` (`"back"`: the screen before; `""`: nowhere), one undo. */
  lead(id: string, to: string): Promise<void>;
  /** The selected frame becomes the screen played first (no other is), one undo. */
  makeStart(): Promise<void>;
  /** The selected frames take a device's size (`constants.ts` `DEVICES`), one undo. */
  setDevice(device: string): Promise<void>;
  /** The selected frame becomes a component (or stops being one), one undo. */
  toggleComponent(): Promise<void>;
  /** An instance of the component `id` beside it (or at `at`), selected; resolves with its id. */
  instance(id: string, at?: Point): Promise<string | null>;
  /** The prototype: the screens clicked through, and exported as a site. */
  prototype: PrototypeService;
}

/** What the service is built from. */
export interface UiUxDeps {
  graph: Pick<GraphMirror, "state">;
  actions: Pick<Actions, "batch" | "updateNode" | "updateGroup" | "addNodes">;
  kinds: Pick<KindsService, "nodes" | "board" | "toDefine">;
  view: Pick<View, "selectNode">;
  board: Pick<Board, "content" | "selected">;
  /** The prototype, made from the screens. */
  prototype: (screens: Pick<UiUx, "active" | "screens">) => PrototypeService;
}

/** Starts UI/UX's service. */
export function createUiux(deps: UiUxDeps, context: PackContext): UiUx {
  const { graph, actions, kinds, view, board } = deps;
  const screens = createMemo(() => screensOf(board.content()));
  const isFrame = (id: string) => board.content().frames.some((f) => f.id === id);
  const groupOf = (id: string) => graph.state.graph.groups.find((g) => g.id === id);
  /** Sends one batch; `false` when there was nothing to send or the core refused. */
  const send = async (edits: GraphEdit[]) => (edits.length ? !!(await actions.batch(edits)) : false);
  /** The edits making the frames `ids` take `device`'s size, keeping their corner. */
  const deviceEdits = (ids: string[], device: string): GraphEdit[] => {
    const size = DEVICES[device];
    return ids.flatMap((id) => {
      const place = board.content().places.get(id);
      if (!place || !groupOf(id)) return [];
      return [{ edit: "update_group", id, patch: { style: { device }, ...(size ? { board: asPatch({ ...place, w: size.w, h: size.h }) } : {}) } } satisfies GraphEdit];
    });
  };
  const service: Omit<UiUx, "prototype"> = {
    active: context.active,
    prototyping: () => context.mode() === PROTOTYPE_MODE,
    setPrototyping: (on) => context.setMode(on ? PROTOTYPE_MODE : DESIGN),
    screens,
    async lead(id, to) {
      if (!graph.state.graph.nodes.some((n) => n.id === id)) return;
      await actions.updateNode(id, { style: { navigate: to, ...(to ? {} : { transition: "" }) } });
    },
    async makeStart() {
      const id = board.selected()[0];
      if (!id || !screens().some((f) => f.id === id)) return void toast("Select a screen (a frame at the top level) to start the prototype there.");
      const edits: GraphEdit[] = screens().flatMap((f) => (f.id === id ? [{ edit: "update_group", id, patch: { style: { start: "yes" } } }] : f.group.style?.start ? [{ edit: "update_group", id: f.id, patch: { style: { start: "" } } }] : []));
      await send(edits);
    },
    async setDevice(device) {
      await send(deviceEdits(board.selected().filter(isFrame), device));
    },
    async toggleComponent() {
      const id = board.selected()[0];
      if (!id || !isFrame(id)) return void toast("Select a frame to make it a component.");
      const on = groupOf(id)?.style?.component === "yes";
      await actions.updateGroup(id, { style: { component: on ? "" : "yes", ...(on ? {} : { start: "", device: "" }) } });
      toast(on ? "It is a frame again; its instances show nothing." : "It is a component: right-click it to make an instance, which follows every change made to it.");
    },
    async instance(id, at) {
      const content = board.content();
      const frame = content.frames.find((f) => f.id === id);
      const kind = kinds.nodes().find((k) => kinds.board(k.id).element === "instance")?.id;
      if (!frame || !kind) return null;
      const { w, h } = frame.place;
      const corner = at ? { x: at.x - w / 2, y: at.y - h / 2 } : { x: frame.place.x + w + INSTANCE_GAP, y: frame.place.y };
      const place = rounded({ ...corner, w, h, z: topZ(content) + 1 });
      const into = frameAround(content, place, new Set([id, ...carried(unwrap(graph.state.graph), [id])]));
      const define = kinds.toDefine([kind], []);
      const ids = await actions.addNodes([{ name: frame.group.name, kind, categories: [], board: place, style: { component: id }, ...(into ? { group: into } : {}) }], [], define);
      const made = ids?.[0] ?? null;
      if (made) view.selectNode(made);
      return made;
    },
  };
  return { ...service, prototype: deps.prototype(service) };
}
